import { useState } from "react";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-rose-500 text-sm";

const EVENT_TYPES = ["Private event", "Festival", "Bar / Club night", "Wedding", "Corporate", "Other"];

export function BookingDialog({ artistId, artistName, available, trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    event_date: "", location: "", event_type: "Private event", budget: "", message: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.name || !f.email || !f.event_date) { toast.error("Name, email and date are required"); return; }
    setBusy(true);
    try {
      await api.post("/bookings", { artist_id: artistId, ...f });
      toast.success(`Booking request sent to ${artistName}! They'll respond to your email.`);
      setOpen(false);
      setF({ ...f, message: "" });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><CalendarCheck className="text-rose-400" size={22} /> Book {artistName}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-zinc-400 -mt-1">{available ? "Tell us about your event and the artist will get back to you." : "This artist isn't actively booking, but you can still send an inquiry."}</DialogDescription>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <input className={inp} placeholder="Your name *" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name" />
          <input className={inp} placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" />
          <input className={inp} placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone" />
          <input type="date" className={inp} value={f.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="booking-date" />
          <input className={inp} placeholder="Location / City" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="booking-location" />
          <select className={inp} value={f.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="booking-type">{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <input className={`${inp} sm:col-span-2`} placeholder="Budget (optional, e.g. $1,500)" value={f.budget} onChange={(e) => set("budget", e.target.value)} data-testid="booking-budget" />
          <textarea rows={3} className={`${inp} sm:col-span-2`} placeholder="Event details, set length, special requests..." value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="booking-message" />
        </div>
        <button onClick={submit} disabled={busy} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg mt-2 disabled:opacity-40" data-testid="booking-submit">{busy ? "Sending..." : "Send booking request"}</button>
      </DialogContent>
    </Dialog>
  );
}
